import { Suspense, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { checkAutoLogin } from "./services/AuthService";
import { isAuthenticated } from "./store/selectors/AuthSelectors";

/* legacy global styles: still required by the interview / checkout surfaces we kept */
import "slick-carousel/slick/slick.css?ver0.1";
import "slick-carousel/slick/slick-theme.css?ver0.1";
import "./vendor/bootstrap-select/dist/css/bootstrap-select.min.css?ver0.1";
import "./css/style.css?ver0.1";

/* modern design system (scoped under .csx, loaded last so it wins) */
import "./modern/theme.css";

/* modern public pages */
import Landing from "./modern/pages/Landing";
import Login from "./modern/pages/Login";
import Registration from "./modern/pages/Registration";
import ForgotPassword from "./modern/pages/ForgotPassword";
import PasswordReset from "./modern/pages/PasswordReset";
import Activate from "./modern/pages/Activate";
import ReactivateLink from "./modern/pages/ReactivateLink";
import Blog from "./modern/pages/Blog";
import BlogDetail from "./modern/pages/BlogDetail";
import PublicJobDetail from "./modern/pages/PublicJobDetail";
import SeoLanding from "./modern/pages/SeoLanding";
import LegalPage from "./modern/pages/LegalPage";
import Contact from "./modern/pages/Contact";

/* authenticated app */
import Markup from "./modern/Markup";
import ErrorBoundary from "./modern/components/ErrorBoundary";

/* legacy public interview surfaces (candidate-facing, keep working as-is) */
import InterviewExam from "./jsx/pages/interviewSystem/InterviewExam";
import InterviewVideo from "./jsx/pages/interviewSystem/interviewVideo";
import Interview from "./jsx/pages/interviewSystem/interview";

function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    return <Component {...props} router={{ location, navigate, params }} />;
  }
  return ComponentWithRouterProp;
}

function Loader() {
  return (
    <div className="csx">
      <div className="csx-page" style={{ alignItems: "center", justifyContent: "center" }}>
        <span className="spinner spinner-lg" />
      </div>
    </div>
  );
}

function App(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAutoLogin(dispatch, navigate, location.pathname);
  }, [dispatch, navigate, location.pathname]);

  useEffect(() => {
    /* Public / marketing pages (home, login, legal, etc.) must render in the
       SAME theme the app uses, so signing in never flips light<->dark
       (Pavithra QA #2). AppShell persists the user's choice in localStorage
       ("cs_theme", default light); mirror it here for the signed-out pages.
       Once authenticated, AppShell owns the attribute. */
    if (!props.isAuthenticated) {
      const stored = localStorage.getItem("cs_theme");
      document.documentElement.setAttribute("data-cs-theme", stored === "dark" ? "dark" : "light");
    }
  }, [props.isAuthenticated]);

  const publicRoutes = (
    <Routes>
      <Route path="/" element={<Landing />} />
      {/* keyword SEO landing pages */}
      <Route path="/ai-resume-builder" element={<SeoLanding slug="ai-resume-builder" />} />
      <Route path="/resume-tailoring" element={<SeoLanding slug="resume-tailoring" />} />
      <Route path="/ai-job-search" element={<SeoLanding slug="ai-job-search" />} />
      <Route path="/mock-interview-practice" element={<SeoLanding slug="mock-interview-practice" />} />
      <Route path="/interview-preparation" element={<SeoLanding slug="interview-preparation" />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blogDetailed" element={<BlogDetail />} />
      {/* legal / policy pages */}
      <Route path="/privacy" element={<LegalPage docKey="privacy" />} />
      <Route path="/terms" element={<LegalPage docKey="terms" />} />
      <Route path="/cookies" element={<LegalPage docKey="cookies" />} />
      <Route path="/gdpr" element={<LegalPage docKey="gdpr" />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/job/detailed" element={<PublicJobDetail />} />
      <Route path="/resetpassword" element={<PasswordReset />} />
      <Route path="/reactivate-link" element={<ReactivateLink />} />
      <Route path="/activate" element={<Activate />} />
      <Route path="/interview" element={<Interview />} />
      <Route path="/interview-Candidate-exam" element={<InterviewExam />} />
      <Route path="/interview-Video-exam" element={<InterviewVideo />} />
      <Route path="/login" element={<Login />} />
      <Route path="/page-register" element={<Registration />} />
      <Route path="/page-forgot-password" element={<ForgotPassword />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <Suspense fallback={<Loader />}>
          {props.isAuthenticated ? (
            <Markup />
          ) : (
            <div className={location.pathname === "/interview-Video-exam" ? "vh-100 videoInterviewPage" : ""}>
              {publicRoutes}
            </div>
          )}
        </Suspense>
        <ToastContainer position="bottom-right" autoClose={3500} hideProgressBar newestOnTop closeOnClick theme="light" />
      </ErrorBoundary>
    </HelmetProvider>
  );
}

const mapStateToProps = (state) => ({
  isAuthenticated: isAuthenticated(state),
});

export default withRouter(connect(mapStateToProps)(App));
